import { posthog } from 'posthog-js'

import authService from '~/services/auth.service'
import appConfig from '~/config/app'

import { ConsentCategories, EcommerceEvent, Event, GlobalEvents } from './types/analytics.type'

import trackBQEvents from './analyticsBQ'

export enum GenericEvents {
  CTA_CLICK = 'cta_click',
  SECTION_VIEW = 'section_view',
  BUTTON_CLICK = 'button_click',
  TAB_CHANGE = 'change_tab',
  FILTER_CHANGE = 'filter_change',
  REWARDS = 'rewards',
  MODAL_VIEW = 'modal_view',
  VIDEO_VIEW = 'video_view',
}
interface IAnalyticsEvent {
  productName: string
  logEvent: (event: Event) => void
}

let consentGranted: undefined | boolean
// store events that fired before consent flow
let tempEventStash: Event[] = []
abstract class AnalyticsEvents_V2 implements IAnalyticsEvent {
  public productName: string

  public constructor(eventCategory: string) {
    this.productName = eventCategory
  }

  public static setConsentStatus(status: boolean) {
    consentGranted = status

    if (status) {
      setTimeout(() => {
        AnalyticsEvents_V2.logStashedEvents()
      }, 0)
    } else {
      // Clean event stash if consent is not granted
      tempEventStash = []
    }
  }

  public static getConsentStatus(): boolean | undefined {
    return consentGranted || window?.CookieInformation?.getConsentGivenFor?.(ConsentCategories.Statistical)
  }

  public static grantAllConsent(): void {
    window.gtag('consent', 'update', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: 'granted',
      functionality_storage: 'granted',
      personalization_storage: 'granted',
    })

    Object.values(ConsentCategories).forEach((category) => {
      // eslint-disable-next-line fp/no-mutating-methods
      window.dataLayer.push({ event: category })
    })
  }

  public static async logStashedEvents(): Promise<void> {
    const tempEventStashCopy = [...tempEventStash]
    tempEventStash = []
    // eslint-disable-next-line fp/no-loops, no-restricted-syntax
    for (const event of tempEventStashCopy) {
      // No school gate needed — logEvent returns before stashing, so there is nothing to flush.
      // eslint-disable-next-line fp/no-mutating-methods
      window.dataLayer.push(event)
      await trackBQEvents(event)
    }
  }

  public async logEvent(eventData: Event): Promise<void> {
    if (typeof window === 'undefined') return

    // The school platform sends no product events anywhere; one gate here covers every subclass
    // and call site. The one exception is the school feedback popup, which captures its answers to
    // PostHog directly (`useSchoolFeedbackModal`), as ClickUp 869fnnt9g asks. Above the stash on purpose: nothing resolves consent in that build, so
    // `consentGranted` stays undefined and the stash would otherwise grow unbounded.
    if (appConfig.isSchoolPlatform) return

    const { event, ...rest } = eventData

    // Redundant under the gate above and kept deliberately: PostHog stays initialized and opted in
    // for schools, so this is the one line that would put product events on a live third-party
    // connection if that gate were ever moved.
    if (event !== GlobalEvents.PAGEVIEW && !appConfig.isSchoolPlatform) posthog.capture(event, rest)

    const shouldLog = consentGranted || window?.CookieInformation?.getConsentGivenFor?.(ConsentCategories.Statistical)

    // Consent status has not been set yet
    if (consentGranted === undefined) {
      // eslint-disable-next-line fp/no-mutating-methods
      tempEventStash.push(eventData)
      return
    }

    if (!shouldLog) return

    AnalyticsEvents_V2.logStashedEvents()

    await this.logEventWithUserData(eventData)
  }

  public logButtonClick(buttonDescription: string, label?: string): void {
    this.logEvent({ event: GenericEvents.BUTTON_CLICK, title: buttonDescription, label })
  }

  public logCtaClick(ctaDescription: string, label?: string): void {
    this.logEvent({ event: GenericEvents.CTA_CLICK, title: ctaDescription, label })
  }

  public logModalView(modalName: string): void {
    this.logEvent({ event: GenericEvents.MODAL_VIEW, title: modalName })
  }

  public logCustomizedEvent(event: string, dimensions: Record<string, string>): void {
    this.logEvent({
      event,
      ...dimensions,
    })
  }

  public logCopyToClipboardViaButton(description: string): void {
    const event = {
      event: GlobalEvents.COPY_TO_CLIPBOARD,
      info: description,
    }

    this.logEvent(event)
  }

  public logEcommerceEvent(event: EcommerceEvent): void {
    const product = 'eCommerce'
    const { eventParams, ...rest } = event

    if (typeof window !== undefined) {
      // eslint-disable-next-line fp/no-mutating-methods
      window.dataLayer.push({ ecommerce: null }) // clear up
      this.logEvent({
        product,
        ...rest,
        ...eventParams,
      })
    }
  }

  public logTabChange(tabPanelName: string, tabName: string): void {
    this.logEvent({
      event: GenericEvents.TAB_CHANGE,
      title: tabPanelName,
      value: tabName,
    })
  }

  public logFilterChange(filterName: string, filterValue: string): void {
    this.logEvent({
      event: GenericEvents.FILTER_CHANGE,
      title: filterName,
      value: filterValue,
    })
  }

  public logPageView(uri: string, title: string, view?: string): void {
    // eslint-disable-next-line fp/no-mutating-methods
    const event = {
      event: GlobalEvents.PAGEVIEW,
      page_uri: uri,
      page_title: title,
      view,
    }

    this.logEvent(event)
  }

  public logRewards(
    rewards: { title: string; gems?: number; kudos?: number; experience_points?: number } = {
      gems: 0,
      kudos: 0,
      experience_points: 0,
      title: 'generic',
    },
    params: Record<string, string> = {},
  ): void {
    if (!rewards.gems && !rewards.kudos && !rewards.experience_points) {
      throw new Error('Rewards must have gems, kudos and experience_points')
    }

    this.logEvent({
      event: GenericEvents.REWARDS,
      ...rewards,
      ...params,
    })
  }

  private async logEventWithUserData(eventData: Event): Promise<void> {
    const userId = authService.getUserId()

    const eventWithUserData = {
      product: this.productName,
      user_id: userId,
      ...eventData,
    }

    // Unreachable in the school platform — logEvent, the only caller, returns before this.
    // eslint-disable-next-line fp/no-mutating-methods
    window.dataLayer.push(eventWithUserData)
    await trackBQEvents(eventWithUserData)
  }
}

export default AnalyticsEvents_V2
